/** The comparison value on a DATE condition, in the only two forms the engine can read.
 *
 *  The engine resolves a filter value at EVALUATION time, not at save time (a workflow filter is
 *  the stored thing, read months later, so an age rule has to recompute its cutoff on every pass).
 *  Its grammar is `$now` with an optional interval — `$now-18y`, `$now+6m`, `$now-30d` — plus plain
 *  ISO dates, which it compares as text.
 *
 *  Everything else is UNREADABLE, and unreadable is dangerous rather than inert: the engine falls
 *  back to text ordering, every ISO date starts with a digit, and digits sort before letters — so
 *  `now-25y` (the same rule with the `$` missed) matches every record that has the field at all.
 *  Measured on dev 2026-08-28: it selected a 6 year old against a 25 year rule. Filed as TRK-5617.
 *  Until that lands the builder must not let anyone author one by hand, which is what this parser
 *  and the editor above it exist to prevent.
 */

export type DateFilterUnit = `d` | `m` | `y`
export type DateFilterDirection = `before` | `after`

export type DateFilterValue =
  /** `$now` — the day the workflow runs. */
  | { kind: `today` }
  /** `$now-18y` — an interval either side of the day the workflow runs. */
  | { kind: `relative`; direction: DateFilterDirection; amount: number; unit: DateFilterUnit }
  /** A fixed calendar day, stored as `YYYY-MM-DD`. Goes stale the moment the rule is about age. */
  | { kind: `absolute`; date: string }
  /** Anything the engine cannot read as a date. Kept verbatim so nothing is silently rewritten. */
  | { kind: `unreadable`; value: string }

export const DATE_FILTER_UNITS: { value: DateFilterUnit; label: string }[] = [
  { value: `d`, label: `days` },
  { value: `m`, label: `months` },
  { value: `y`, label: `years` },
]

// `m` is MONTHS here, not minutes — the engine's own unit map. Wait durations use `m` for minutes
// in the same builder, so never share a unit constant between the two.
const INTERVAL = /^([+-])(\d+)([ymd])$/
// The shape deliberately matches the engine's own `isDateString` (`^\d{4}-\d{2}-\d{2}(T|$)`), so the
// builder never refuses a value the engine would accept. The time suffix is left opaque for the same
// reason: an offset like `T00:00:00+05:00` is a legitimate stored value, and only the day part
// decides a date comparison, since the engine compares these as text.
const ISO_SHAPE = /^(\d{4})-(\d{2})-(\d{2})(T|$)/

/** Whether a Y-M-D triple is a day that exists. The shape check alone accepts `2024-02-30` and
 *  `2024-13-99`, and calling those readable would wave through exactly the class of value this
 *  module exists to catch. Round-tripping through UTC gets leap years right for free. */
function isRealCalendarDay(year: number, month: number, day: number): boolean {
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

export function parseDateFilterValue(value: string | undefined): DateFilterValue {
  const raw = value?.trim() ?? ``
  if (raw === ``) return { kind: `unreadable`, value: `` }
  if (raw === `$now`) return { kind: `today` }
  if (raw.startsWith(`$now`)) {
    const match = INTERVAL.exec(raw.slice(4))
    if (!match) return { kind: `unreadable`, value: raw }
    const [, sign, amount, unit] = match
    // `$now-0y` and `$now+0d` both mean today; keep one representation so the editor round-trips.
    if (Number(amount) === 0) return { kind: `today` }
    return {
      kind: `relative`,
      direction: sign === `-` ? `before` : `after`,
      amount: Number(amount),
      unit: unit as DateFilterUnit,
    }
  }
  const iso = ISO_SHAPE.exec(raw)
  if (iso) {
    const [, year, month, day] = iso
    if (isRealCalendarDay(Number(year), Number(month), Number(day))) {
      return { kind: `absolute`, date: `${year}-${month}-${day}` }
    }
  }
  return { kind: `unreadable`, value: raw }
}

export function formatDateFilterValue(value: DateFilterValue): string {
  switch (value.kind) {
    case `today`:
      return `$now`
    case `relative`:
      return `$now${value.direction === `before` ? `-` : `+`}${value.amount}${value.unit}`
    case `absolute`:
      return value.date
    case `unreadable`:
      return value.value
  }
}

/** True when the engine can read this value as a date. An empty value counts as incomplete rather
 *  than wrong, so callers that only want to block real mistakes should check emptiness first. */
export function isReadableDateFilterValue(value: string | undefined): boolean {
  return parseDateFilterValue(value).kind !== `unreadable`
}

const UNIT_LABEL: Record<DateFilterUnit, string> = { d: `day`, m: `month`, y: `year` }

/** The value as a person reads it on a node card: "18 years before today". */
export function describeDateFilterValue(value: string | undefined): string {
  const parsed = parseDateFilterValue(value)
  switch (parsed.kind) {
    case `today`:
      return `today`
    case `relative`: {
      const unit = UNIT_LABEL[parsed.unit]
      return `${parsed.amount} ${parsed.amount === 1 ? unit : `${unit}s`} ${parsed.direction} today`
    }
    case `absolute`:
      return parsed.date
    case `unreadable`:
      return parsed.value
  }
}
